import { IconName } from '@billy/design-system'
import { TFunction } from 'i18next'

import { VatDeclarationStatus } from '../../../enums/vatDeclarationStatus'
import { VatDeclarationSubmission } from '../../../types/vatDeclarationSubmission'
import { getSubmitButtonVariant, SubmitButtonVariant } from '../../../utils/getSubmitButtonVariant'
import { shouldUseDKReportContent } from '../../../utils/isSupportedCountryId'
import { VatDeclarationsTableStaticActionType } from '../enums/vatDeclarationsStaticActionType'
import { VatDeclarationsTableStaticActionItem } from '../types/vatDeclarationsTableStaticActionItem'

interface GetVatDeclarationsTableStaticActionsProps {
  countryId?: string
  correctionNo?: number
  isPaid: boolean
  isSettled: boolean
  isSkatConnected?: boolean
  submission?: VatDeclarationSubmission
  status: VatDeclarationStatus
  t: TFunction
}

export const getVatDeclarationsTableStaticActions = ({
  countryId,
  correctionNo,
  isPaid,
  isSettled,
  isSkatConnected,
  submission,
  status,
  t,
}: GetVatDeclarationsTableStaticActionsProps) => {
  const isReadyToSubmit =
    status === VatDeclarationStatus.ReadyToSubmit || status === VatDeclarationStatus.OverdueSubmission
  const isReadyToPay = status === VatDeclarationStatus.ReadyToPay || status === VatDeclarationStatus.OverduePayment
  const canBeVoided = isSettled && !isPaid
  const isDKReportActions = shouldUseDKReportContent(countryId)

  const submitButtonVariant = getSubmitButtonVariant({
    countryId,
    correctionNo,
    isSkatConnected,
    submission,
    status,
  })

  const items: VatDeclarationsTableStaticActionItem[] = [
    {
      children: t('vat_declarations.actions.export'),
      iconLeft: 'download' as IconName,
      id: VatDeclarationsTableStaticActionType.Export,
      value: VatDeclarationsTableStaticActionType.Export,
    },
  ]

  if (isReadyToSubmit && (submitButtonVariant !== SubmitButtonVariant.Integration || isDKReportActions)) {
    items.unshift({
      children: t('vat_declarations.submit_manually'),
      iconLeft: 'squarePen' as IconName,
      id: VatDeclarationsTableStaticActionType.SubmitManually,
      value: VatDeclarationsTableStaticActionType.SubmitManually,
    })
  }

  if (isReadyToSubmit && submitButtonVariant === SubmitButtonVariant.Integration) {
    items.unshift({
      children: t('vat_declarations.table.static_actions.settle'),
      iconLeft: 'fileCheck' as IconName,
      id: VatDeclarationsTableStaticActionType.Settle,
      value: VatDeclarationsTableStaticActionType.Settle,
    })
  }

  if (isReadyToPay) {
    items.unshift({
      children: t('vat_declarations.table.static_actions.registerPayment'),
      iconLeft: 'handCoins' as IconName,
      id: VatDeclarationsTableStaticActionType.RegisterPayment,
      value: VatDeclarationsTableStaticActionType.RegisterPayment,
    })
  }

  if (isPaid) {
    items.unshift({
      children: t('vat_declarations.table.static_actions.voidPayment'),
      iconLeft: 'banknoteX' as IconName,
      id: VatDeclarationsTableStaticActionType.VoidPayment,
      value: VatDeclarationsTableStaticActionType.VoidPayment,
    })
  }

  if (canBeVoided) {
    items.unshift({
      children: t('vat_declarations.actions.void_settlement'),
      iconLeft: 'receiptEuro' as IconName,
      id: VatDeclarationsTableStaticActionType.VoidSettlement,
      value: VatDeclarationsTableStaticActionType.VoidSettlement,
    })
  }

  return items
}
